import {  collection, addDoc, updateDoc, doc, query, setDoc, orderBy, onSnapshot, increment, getDoc, limit, startAfter, where, Timestamp, runTransaction, getDocs, collectionGroup } from 'firebase/firestore';
import { db } from "../firebase-config";
  
export class CommunityBoardDAO {

  static calculateHotScore(vote_score, createdAt) {
    const order = Math.log10(Math.max(Math.abs(vote_score), 1));
    const sign = vote_score > 0 ? 1 : vote_score < 0 ? -1 : 0;
    const seconds = createdAt.getTime() / 1000 - 1134028003;
    return Math.round(sign * order + seconds / 45000, 7);
  }

  static getPostsQuery(sortBy, timeFilter) {
    let baseQuery = collection(db, 'community_board');
    let constraints = [];

    if (timeFilter !== 'all') {
      const cutoffDate = new Date();
      switch (timeFilter) {
        case 'today':
          cutoffDate.setDate(cutoffDate.getDate() - 1);
          break;
        case 'week':
          cutoffDate.setDate(cutoffDate.getDate() - 7);
          break;
        case 'month':
          cutoffDate.setMonth(cutoffDate.getMonth() - 1);
          break;
        case 'year':
          cutoffDate.setFullYear(cutoffDate.getFullYear() - 1);
          break;
      }
      constraints.push(where('created_at', '>=', Timestamp.fromDate(cutoffDate)));
    }

    switch (sortBy) {
      case 'hot':
        constraints.push(orderBy('hot_score', 'desc'));
        break;
      case 'new':
        constraints.push(orderBy('created_at', 'desc'));
        break;
      case 'top':
        constraints.push(orderBy('vote_score', 'desc'));
        break;
    }

    return query(baseQuery, ...constraints);
  }

  static async createPost(postData) {
    try {
      const now = new Date();
      const postDoc = {
        ...postData,
        created_at: now,
        vote_score: 1,
        comment_count: 0,
        deleted: false,
        hot_score: this.calculateHotScore(1, now),
        rising_score: 0,
        controversy_score: 0,
        category: postData.category || 'General',
        replies: []
      };
      const postRef = await addDoc(collection(db, 'community_board'), postDoc);
      const userVoteRef = doc(db, "users", postData.creator_id, "community_board", postRef.id);
      await setDoc(userVoteRef, { upvote: true, created_at: now});
      return postRef.id;
    } catch (error) {
      console.error('Error in createPost:', error);
      throw error;
    }
  }

  static async voteOnPost(postId, userId, _incrementValue, upVote) {
    try {
      await runTransaction(db, async (transaction) => {
        // Try regular posts first
        let postRef = doc(db, 'community_board', postId);
        let postDoc = await transaction.get(postRef);
        
        // If not found in regular posts, try stickied posts
        if (!postDoc.exists()) {
          postRef = doc(db, 'community_board_stickied_posts', postId);
          postDoc = await transaction.get(postRef);
        }
        
        if (!postDoc.exists()) throw new Error('Post does not exist');

        const userVoteRef = doc(db, "users", userId, "community_board", postId);
        const userVoteDoc = await transaction.get(userVoteRef);
        
        const post = postDoc.data();

        let voteChange = 0;
        
        if (userVoteDoc.exists()) {
          const existingVote = userVoteDoc.data().upvote;

          if (existingVote === upVote) {
            // Removing a vote
            // If removing upvote: -1, if removing downvote: +1
            voteChange = existingVote ? -1 : 1;

            transaction.set(userVoteRef, {
              upvote: null,
              created_at: userVoteDoc.data().created_at,
              updated_at: Timestamp.now()
            });
          } else if (existingVote === null) {
            // New vote after a vote was removed
            voteChange = upVote ? 1 : -1;

            transaction.set(userVoteRef, { 
              upvote: upVote,
              created_at: userVoteDoc.data().created_at,
              updated_at: Timestamp.now()
            });
          } else {
            voteChange = upVote ? 2 : -2;

            transaction.set(userVoteRef, { 
              upvote: upVote,
              created_at: userVoteDoc.data().created_at,
              updated_at: Timestamp.now()
            });
          }
        } else {
          // First vote ever
          voteChange = upVote ? 1 : -1;

          transaction.set(userVoteRef, { 
            upvote: upVote,
            created_at: Timestamp.now(),
            updated_at: Timestamp.now()
          });
        }

        transaction.update(postRef, {
          vote_score: increment(voteChange),
          hot_score: this.calculateHotScore(post.vote_score + voteChange, post.created_at.toDate())
        });
      });
    } catch (error) {
      console.error('Error in voteOnPost:', error);
      throw error;
    }
  }

  static subscribeToUserVotes(uid, callback) {
    // Subscribe to post votes
    const postVotesQuery = query(collection(db, "users", uid, "community_board"));
    const postVotesUnsubscribe = onSnapshot(postVotesQuery, async (postSnapshot) => {
      const votesData = {};

      // Get post votes
      postSnapshot.docs.forEach(doc => {
        votesData[doc.id] = doc.data().upvote;
      });

      // Get reply votes for each post
      for (const postDoc of postSnapshot.docs) {
        const repliesRef = collection(db, "users", uid, "community_board", postDoc.id, "replies");
        const repliesSnapshot = await getDocs(repliesRef);
        
        repliesSnapshot.docs.forEach(replyDoc => {
          votesData[replyDoc.id] = replyDoc.data().upvote;
        });
      }

      callback(votesData);
    });

    return postVotesUnsubscribe;
  }

  static async addMessage(postId, messageData, parentReplyId = null) {
    try {
      // Get post and determine which collection it's in
      const { postDoc, collectionName } = await this.getPost(postId);
      if (!postDoc) {
        throw new Error('Post not found');
      }

      const postRef = doc(db, collectionName, postId);
      let newReplyId;
      let newReplyData;
      
      if (!parentReplyId) {
        const repliesRef = collection(postRef, 'replies');
        const newReplyRef = doc(repliesRef);
        newReplyId = newReplyRef.id;
        newReplyData = { 
          creator: messageData.creator || 'Anonymous', 
          creator_id: messageData.creator_id, 
          message: messageData.message, 
          created_at: Timestamp.now(), 
          vote_score: 1, 
          parent_id: null, 
        };
        await setDoc(newReplyRef, newReplyData);
        await updateDoc(postRef, { comment_count: increment(1)});
      } else {
        const replyPath = await this.findReplyPath(postId, parentReplyId, collectionName);
        let currentRef = postRef;
        for (let i = 0; i < replyPath.length; i++) {
          if (replyPath[i] === 'replies') currentRef = collection(currentRef, 'replies');
          else currentRef = doc(currentRef, replyPath[i]);
        }
        const repliesRef = collection(currentRef, 'replies');
        const newReplyRef = doc(repliesRef);
        newReplyId = newReplyRef.id;
        newReplyData = {
          creator: messageData.creator || 'Anonymous', 
          creator_id: messageData.creator_id, 
          message: messageData.message, 
          created_at: Timestamp.now(), 
          vote_score: 1, 
          parent_id: parentReplyId, 
          replies: []
        };
        await setDoc(newReplyRef, newReplyData);
        await updateDoc(postRef, { comment_count: increment(1)});
      }

      // Save the reply in the user's collection if the user is logged in
      if (messageData.creator_id) {
        const userReplyRef = doc(db, 'users', messageData.creator_id, 'community_board', postId, 'replies', newReplyId);
        await setDoc(userReplyRef, {
          ...newReplyData,
          postId: postId,
          postTopic: postDoc.data().topic
        });
      }

      return newReplyId;
    } catch (error) {
      throw new Error(`Failed to add message: ${error.message}`);
    }
  }

  static async findReplyPath(postId, replyId, collectionName = 'community_board') {
    const postRef = doc(db, collectionName, postId);
    
    async function searchReplies(currentRef, currentPath = ['replies'], depth = 0) {
      const repliesQuery = collection(currentRef, 'replies');
      const repliesSnapshot = await getDocs(repliesQuery);
      for (const reply of repliesSnapshot.docs) {
        if (reply.id === replyId) return [...currentPath, reply.id];
        const nestedPath = [...currentPath, reply.id, 'replies'];
        const nestedFound = await searchReplies(reply.ref, nestedPath, depth + 1);
        if (nestedFound) return nestedFound;
      }
      return null;
    }
    const foundPath = await searchReplies(postRef);
    if (!foundPath) return ['replies', replyId];      
    return foundPath;
  }

  static async voteOnMessage(postId, messageId, userId, isUpvote, parentMessageId = null) {
    try {
      await runTransaction(db, async (transaction) => {
        // Get post and determine which collection it's in
        const { postDoc, collectionName } = await this.getPost(postId);
        if (!postDoc) {
          throw new Error('Post not found');
        }

        // Path for the message in the appropriate collection
        const messagePath = parentMessageId 
          ? `${collectionName}/${postId}/replies/${parentMessageId}/replies/${messageId}`
          : `${collectionName}/${postId}/replies/${messageId}`;

        // Path for storing the vote in user's document
        const userVotePath = `users/${userId}/community_board/${postId}/replies/${messageId}`;
        const userPostVoteRef = doc(db, `users/${userId}/community_board/${postId}`);

        const messageRef = doc(db, messagePath);
        const userVoteRef = doc(db, userVotePath);

        const messageDoc = await transaction.get(messageRef);
        const userVoteDoc = await transaction.get(userVoteRef);
        const userPostVoteDoc = await transaction.get(userPostVoteRef);

        if (!messageDoc.exists()) {
          console.error('Message not found at path:', messagePath);
          throw new Error('Message does not exist');
        }

        // If the parent post vote document doesn't exist, create it with null vote
        if (!userPostVoteDoc.exists()) {
          transaction.set(userPostVoteRef, {
            upvote: null,
            created_at: Timestamp.now(),
            updated_at: Timestamp.now()
          });
        }

        let voteChange = 0;

        if (userVoteDoc.exists()) {
          const existingVote = userVoteDoc.data().upvote;
          
          if (existingVote === isUpvote) {
            // Removing a vote
            // If removing upvote: -1, if removing downvote: +1
            voteChange = existingVote ? -1 : 1;

            transaction.set(userVoteRef, { 
              upvote: null,
              created_at: userVoteDoc.data().created_at,
              updated_at: Timestamp.now()
            });
          } else if (existingVote === null) {
            // New vote after a vote was removed
            voteChange = isUpvote ? 1 : -1;
            transaction.set(userVoteRef, { 
              upvote: isUpvote,
              created_at: userVoteDoc.data().created_at,
              updated_at: Timestamp.now()
            });
          } else {
            // Changing vote type
            // If going from downvote to upvote: +2
            // If going from upvote to downvote: -2
            voteChange = isUpvote ? 2 : -2;

            transaction.set(userVoteRef, { 
              upvote: isUpvote,
              created_at: userVoteDoc.data().created_at,
              updated_at: Timestamp.now()
            });
          }
        } else {
          // First vote ever
          voteChange = isUpvote ? 1 : -1;

          transaction.set(userVoteRef, { 
            upvote: isUpvote,
            created_at: Timestamp.now(),
            updated_at: Timestamp.now()
          });
        }

        transaction.update(messageRef, {
          vote_score: increment(voteChange)
        });
      });
    } catch (error) {
      console.error('Error in voteOnMessage:', error);
      throw error;
    }
  }

  static createMessagesQuery(postId, collectionName, options = {}) {
    const { sortBy = 'top', lastVisible = null } = options;

    const messagesRef = collection(db, collectionName, postId, 'replies');
    let constraints = [];

    switch (sortBy) {
      case 'new':
        constraints.push(orderBy('created_at', 'desc'));
        break;
      case 'top':
        constraints.push(orderBy('vote_score', 'desc'));
        break;
    }
    constraints.push(limit(10));
    if (lastVisible) constraints.push(startAfter(lastVisible));
    return query(messagesRef, ...constraints);
  }

  static async fetchMessagesForPost(postId, options = {}) {
    try {
      // First determine which collection the post is in
      const { postDoc, collectionName } = await this.getPost(postId);
      if (!postDoc) {
        throw new Error('Post not found');
      }

      const messagesQuery = this.createMessagesQuery(postId, collectionName, options);
      const snapshot = await getDocs(messagesQuery);
      const messages = await Promise.all(
        snapshot.docs.map(async (doc) => {
          const messageData = { id: doc.id, ...doc.data() };
          const repliesData = await this.fetchNestedReplies(doc.ref);
          messageData.replies = repliesData.replies;
          messageData.hasMoreReplies = repliesData.hasMoreReplies;
          messageData.totalReplies = repliesData.totalReplies;
          return messageData;
        })
      );

      const lastVisible = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;
      let hasMore = false;

      if (lastVisible) {
        const nextBatchQuery = this.createMessagesQuery(postId, collectionName, { 
          sortBy: options.sortBy, 
          timeFilter: options.timeFilter, 
          lastVisible: lastVisible 
        });
        const nextBatchSnapshot = await getDocs(nextBatchQuery);
        hasMore = !nextBatchSnapshot.empty;
      }

      return { messages, lastVisible, hasMore };
    } catch (error) {
      console.error('Error fetching messages:', error);
      return { messages: [], lastVisible: null, hasMore: false };
    }
  }

  static async updateHotScore(postId, votes, createdAt) {
    try {
      const createdAtDate = createdAt instanceof Timestamp ? createdAt.toDate() : createdAt;
      const hotScore = this.calculateHotScore(votes, createdAtDate);
      
      await updateDoc(doc(db, 'community_board', postId), { hot_score: hotScore});
    } catch (error) {
      console.error('Error updating hot score:', error);
      throw error;
    }
  }

  static async getPost(postId) {
    // First try to get from regular posts
    const postRef = doc(db, 'community_board', postId);
    const postDoc = await getDoc(postRef);
    
    if (postDoc.exists()) {
      return { postDoc, collectionName: 'community_board' };
    }
    
    // If not found, try stickied posts
    const stickiedPostRef = doc(db, 'community_board_stickied_posts', postId);
    const stickiedPostDoc = await getDoc(stickiedPostRef);
    
    if (stickiedPostDoc.exists()) {
      return { postDoc: stickiedPostDoc, collectionName: 'community_board_stickied_posts' };
    }

    return { postDoc: null, collectionName: null };
  }

  static async updatePost(postId, updateData) {
    try {
      const postRef = doc(db, 'community_board', postId);
      await updateDoc(postRef, updateData);
    } catch (error) {
      console.error('Error updating post:', error);
      throw new Error('Failed to update post');
    }
  }

  static async fetchNestedReplies(replyRef) {
    try {
      const repliesRef = collection(replyRef, 'replies');
      const repliesQuery = query(repliesRef, limit(5));
      const repliesSnapshot = await getDocs(repliesQuery);
      let hasMoreReplies = false;
      if (repliesSnapshot.docs.length > 0) {
        const nextPageQuery = query(repliesRef, limit(5), startAfter(repliesSnapshot.docs[repliesSnapshot.docs.length - 1]));
        const nextBatchSnapshot = await getDocs(nextPageQuery);
        hasMoreReplies = !nextBatchSnapshot.empty;
      }
      
      const replies = await Promise.all(
        repliesSnapshot.docs.map(async (replyDoc) => {
          const replyData = {
            id: replyDoc.id,
            ...replyDoc.data()
          };
          
          // Recursively fetch nested replies
          const nestedRepliesData = await this.fetchNestedReplies(replyDoc.ref);
          replyData.replies = nestedRepliesData.replies;
          replyData.hasMoreReplies = nestedRepliesData.hasMoreReplies;
          replyData.totalReplies = nestedRepliesData.totalReplies;
          
          return replyData;
        })
      );

      return { 
        replies, 
        hasMoreReplies, 
        totalReplies: repliesSnapshot.size + (hasMoreReplies ? 1 : 0)
      };
    } catch (error) {
      console.error('Error fetching nested replies:', error);
      return {replies: [], hasMoreReplies: false, totalReplies: 0 };
    }
  }

  static createPostQuery(options = {}) {
    const { sortBy = 'hot', timeFilter = 'all', lastVisible = null, showSavedOnly = false, showUserPosts = false, userId = null } = options;
    let baseQuery = collection(db, 'community_board');
    let constraints = [];
    const now = new Date();
    let cutoffDate;

    // Add deleted constraint first
    constraints.push(where('deleted', '==', false));

    // If showing user's posts, add creator_id constraint
    if (showUserPosts && userId) {
      constraints.push(where('creator_id', '==', userId));
    }

    switch (timeFilter) {
      case 'hour':
        cutoffDate = new Date(now.getTime() - 60 * 60 * 1000);
        break;
      case 'today':
        cutoffDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case 'week':
        cutoffDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'month':
        cutoffDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
        break;
      case 'year':
        cutoffDate = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
        break;
      default:
        break;
    }

    if (cutoffDate) constraints.push(where('created_at', '>=', Timestamp.fromDate(cutoffDate)));
    
    let orderByField;
    switch (sortBy) {
      case 'new':
        orderByField = 'created_at';
        break;
      case 'top':
        orderByField = 'vote_score';
        break;
      case 'hot':
        orderByField = 'hot_score';
        break;
      case 'rising':
        orderByField = 'hot_score';
        const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        constraints.push(where('created_at', '>=', Timestamp.fromDate(oneDayAgo)));
        break;
    }

    constraints.push(orderBy(orderByField, 'desc'));
    constraints.push(limit(5));
    if (lastVisible) constraints.push(startAfter(lastVisible));

    return query(baseQuery, ...constraints);
  }

  static getNextTimeFilter(currentFilter) {
    const filterProgression = {
      'today': 'week',
      'week': 'month',
      'month': 'year',
      'year': 'all'
    };
    return filterProgression[currentFilter] || 'all';
  }

  static async fetchPostsWithTimeFilter(options, timeFilter) {
    const modifiedOptions = { ...options, timeFilter };
    let posts = [];
    let lastVisibleDoc = null;
    let stickiedPostsCount = 0;

    // Regular post fetching logic
    if (!modifiedOptions.showSavedOnly) {
      // Only fetch stickied posts if not showing user posts
      if (!modifiedOptions.showUserPosts) {
        const stickiedQuery = query(
          collection(db, 'community_board_stickied_posts')
        );
        const stickiedSnapshot = await getDocs(stickiedQuery);
        const stickiedPosts = stickiedSnapshot.docs.map(doc => ({id: doc.id, ...doc.data(), is_sticky: true}));
        posts = [...stickiedPosts];
        stickiedPostsCount = stickiedPosts.length;
      }
      
      const postQuery = this.createPostQuery(modifiedOptions);
      const snapshot = await getDocs(postQuery);
      const regularPosts = snapshot.docs.map(doc => ({id: doc.id, ...doc.data()}));
      
      posts = [...posts, ...regularPosts];
      lastVisibleDoc = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;
    }

    return { posts, lastVisibleDoc, stickiedPostsCount };
  }

  static async fetchPosts(options = {}) {
    try {
      const { sortBy = 'hot', timeFilter = 'all', lastVisible = null, showSavedOnly = false, showUserPosts = false, userId = null } = options;

      let posts = [];
      let lastVisibleDoc = null;
      let currentTimeFilter = timeFilter;

      // If showing saved posts only, handle differently
      if (showSavedOnly && userId) {
        // Get saved post references with their saved status
        const savedPostsQuery = query(
          collection(db, 'users', userId, 'community_board'),
          where('saved', '==', true)
        );
        const savedPostsSnapshot = await getDocs(savedPostsQuery);
        
        if (!savedPostsSnapshot.empty) {
          // Get the IDs of saved posts
          const savedPostIds = savedPostsSnapshot.docs.map(doc => doc.id);
          
          // Create chunks of 10 IDs (Firestore limit for 'in' queries)
          const chunks = [];
          for (let i = 0; i < savedPostIds.length; i += 10) {
            chunks.push(savedPostIds.slice(i, i + 10));
          }

          // Fetch posts for each chunk
          const fetchPromises = chunks.map(async chunk => {
            // First try regular posts
            const regularPostsQuery = query(
              collection(db, 'community_board'),
              where('__name__', 'in', chunk)
            );
            const regularSnapshot = await getDocs(regularPostsQuery);
            
            // Then try stickied posts
            const stickiedPostsQuery = query(
              collection(db, 'community_board_stickied_posts'),
              where('__name__', 'in', chunk)
            );
            const stickiedSnapshot = await getDocs(stickiedPostsQuery);

            return [
              ...regularSnapshot.docs
                .filter(doc => !doc.data().deleted)
                .map(doc => ({id: doc.id, ...doc.data()})),
              ...stickiedSnapshot.docs
                .filter(doc => !doc.data().deleted)
                .map(doc => ({id: doc.id, ...doc.data(), is_sticky: true}))
            ];
          });

          const results = await Promise.all(fetchPromises);
          posts = results.flat();

          // Sort the posts according to the sortBy parameter
          switch (sortBy) {
            case 'new':
              posts.sort((a, b) => b.created_at.toMillis() - a.created_at.toMillis());
              break;
            case 'top':
              posts.sort((a, b) => (b.vote_score || 0) - (a.vote_score || 0));
              break;
            case 'hot':
              posts.sort((a, b) => (b.hot_score || 0) - (a.hot_score || 0));
              break;
            case 'rising':
              const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
              posts = posts
                .filter(post => post.created_at.toDate() >= oneDayAgo)
                .sort((a, b) => (b.hot_score || 0) - (a.hot_score || 0));
              break;
          }

          // Apply time filter with fuzzy matching
          while (currentTimeFilter !== 'all' && posts.length === 0) {
            const cutoffDate = this.getCutoffDate(currentTimeFilter);
            posts = posts.filter(post => post.created_at.toDate() >= cutoffDate);
            if (posts.length === 0) {
              currentTimeFilter = this.getNextTimeFilter(currentTimeFilter);
            }
          }

          // Handle pagination
          const startIndex = lastVisible ? posts.findIndex(p => p.id === lastVisible.id) + 1 : 0;
          const paginatedPosts = posts.slice(startIndex, startIndex + 5);
          lastVisibleDoc = paginatedPosts[paginatedPosts.length - 1];
          posts = paginatedPosts;
        }
      } else {
        // For regular posts, try each time filter until we find more than just stickied posts
        while (currentTimeFilter !== 'all') {
          const result = await this.fetchPostsWithTimeFilter(options, currentTimeFilter);
          const hasMoreThanStickied = result.posts.length > result.stickiedPostsCount;
          
          posts = result.posts;
          lastVisibleDoc = result.lastVisibleDoc;
          
          // If we have more posts than just stickied posts, we're good
          if (hasMoreThanStickied) {
            break;
          }
          currentTimeFilter = this.getNextTimeFilter(currentTimeFilter);
        }

        // If we still only have stickied posts, try with 'all'
        if (posts.length <= (posts.filter(p => p.is_sticky)?.length || 0)) {
          const result = await this.fetchPostsWithTimeFilter(options, 'all');
          posts = result.posts;
          lastVisibleDoc = result.lastVisibleDoc;
        }
      }

      let hasMore = false;
      if (lastVisibleDoc) {
        if (showSavedOnly) {
          // For saved posts, check if there are more posts after the current page
          const currentIndex = posts.findIndex(p => p.id === lastVisibleDoc.id);
          hasMore = currentIndex < posts.length - 1;
        } else {
          const nextBatchQuery = this.createPostQuery({ 
            ...options,
            timeFilter: currentTimeFilter,
            lastVisible: lastVisibleDoc
          });
          const nextBatchSnapshot = await getDocs(nextBatchQuery);
          hasMore = !nextBatchSnapshot.empty;
        }
      }

      return { posts, lastVisible: lastVisibleDoc, hasMore, effectiveTimeFilter: currentTimeFilter };
    } catch (error) {
      console.error('Error fetching posts:', error);
      return { posts: [], lastVisible: null, hasMore: false, effectiveTimeFilter: 'all' };
    }
  }

  static async updateHotScore(postId, votes, createdAt) {
    const hotScore = this.calculateHotScore(votes, createdAt);
    await updateDoc(doc(db, 'community_board', postId), { hot_score: hotScore });
  }

  static async loadMoreReplies(postId, parentReplyId, lastReplyId, options = {}) {
    const {limit = 3 } = options;
    try {
      const replyPath = await this.findReplyPath(postId, parentReplyId);
      let currentRef = doc(db, 'community_board', postId);
      for (let i = 0; i < replyPath.length; i++) {
        if (replyPath[i] === 'replies') currentRef = collection(currentRef, 'replies');
        else currentRef = doc(currentRef, replyPath[i]);
      }
      const repliesRef = collection(currentRef, 'replies');
      const constraints = [orderBy('score', 'desc'), startAfter(doc(repliesRef, lastReplyId)), limit(5)];
      const repliesQuery = query(repliesRef, ...constraints);
      const snapshot = await getDocs(repliesQuery);
      return snapshot.docs.map(doc => ({id: doc.id, ...doc.data(), replies: []}));
    } catch (error) {
      console.error('Error loading more replies:', error);
      throw error;
    }
  }

  static async savePost(postId, userId, saved) {
    try {
      const docRef = doc(db, 'users', userId, 'community_board', postId);
      await setDoc(docRef, { saved }, { merge: true });
      return true;
    } catch (error) {
      console.error('Error saving post:', error);
      throw error;
    }
  }

  static async softDeletePost(postId) {
    try {
      const { postDoc, collectionName } = await this.getPost(postId);
      if (!postDoc) {
        throw new Error('Post not found');
      }
      
      const postRef = doc(db, collectionName, postId);
      await updateDoc(postRef, {
        deleted: true,
        deleted_at: Timestamp.now()
      });
      return true;
    } catch (error) {
      console.error('Error soft deleting post:', error);
      throw error;
    }
  }

  static async isPostSaved(postId, userId) {
    try {
      const docRef = doc(db, 'users', userId, 'community_board', postId);
      const docSnap = await getDoc(docRef);
      return docSnap.exists() ? docSnap.data()?.saved || false : false;
    } catch (error) {
      console.error('Error checking saved status:', error);
      throw error;
    }
  }

  // Helper method to get cutoff date
  static getCutoffDate(timeFilter) {
    const now = new Date();
    switch (timeFilter) {
      case 'today':
        return new Date(now.getTime() - 24 * 60 * 60 * 1000);
      case 'week':
        return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      case 'month':
        return new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
      case 'year':
        return new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
      default:
        return new Date(0); // Beginning of time
    }
  }

  static async fetchUserComments(userId, options = {}) {
    try {
      const { lastVisible = null, limit: queryLimit = 5 } = options;
      const userCommentsRef = collection(db, 'users', userId, 'community_board');
      const userCommentsQuery = query(userCommentsRef);
      const userCommentsSnapshot = await getDocs(userCommentsQuery);

      // Get all posts where user has commented
      const postIds = userCommentsSnapshot.docs.map(doc => doc.id);
      
      if (postIds.length === 0) {
        return { comments: [], lastVisible: null, hasMore: false };
      }

      // Create chunks of 10 IDs (Firestore limit for 'in' queries)
      const chunks = [];
      for (let i = 0; i < postIds.length; i += 10) {
        chunks.push(postIds.slice(i, i + 10));
      }

      // Fetch posts and their comments
      const fetchPromises = chunks.map(async chunk => {
        const comments = [];

        // Fetch from regular posts
        const regularPostsQuery = query(collection(db, 'community_board'), where('__name__', 'in', chunk));
        const regularSnapshot = await getDocs(regularPostsQuery);
        
        // Fetch from stickied posts
        const stickiedPostsQuery = query(collection(db, 'community_board_stickied_posts'), where('__name__', 'in', chunk));
        const stickiedSnapshot = await getDocs(stickiedPostsQuery);

        const allPosts = [
          ...regularSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })),
          ...stickiedSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), is_sticky: true }))
        ];

        // For each post, get the user's comments
        for (const post of allPosts) {
          const userRepliesRef = collection(db, 'users', userId, 'community_board', post.id, 'replies');
          const userRepliesSnapshot = await getDocs(userRepliesRef);
          
          userRepliesSnapshot.docs.forEach(replyDoc => {
            comments.push({
              id: replyDoc.id,
              postId: post.id,
              postTopic: post.topic,
              ...replyDoc.data(),
              post_is_sticky: post.is_sticky
            });
          });
        }

        return comments;
      });

      const results = await Promise.all(fetchPromises);
      let allComments = results.flat();

      // Sort comments by creation date
      allComments.sort((a, b) => b.created_at.toMillis() - a.created_at.toMillis());

      // Handle pagination
      const startIndex = lastVisible ? allComments.findIndex(c => c.id === lastVisible.id) + 1 : 0;
      const paginatedComments = allComments.slice(startIndex, startIndex + queryLimit);
      const lastVisibleDoc = paginatedComments[paginatedComments.length - 1];
      const hasMore = startIndex + queryLimit < allComments.length;

      return {
        comments: paginatedComments,
        lastVisible: lastVisibleDoc,
        hasMore
      };
    } catch (error) {
      console.error('Error fetching user comments:', error);
      return { comments: [], lastVisible: null, hasMore: false };
    }
  }

  static async reportContent(contentId, reporterId, contentType, reason) {
    try {
      const reportRef = collection(db, 'community_board_reports');
      await addDoc(reportRef, {
        content_id: contentId,
        content_type: contentType, // 'post' or 'comment'
        reporter_id: reporterId,
        reason: reason,
        status: 'pending', // pending, reviewed, dismissed
        created_at: Timestamp.now()
      });
      return true;
    } catch (error) {
      console.error('Error reporting content:', error);
      throw error;
    }
  }

  static async getReports(contentId) {
    try {
      const reportsRef = collection(db, 'community_board_reports');
      const q = query(reportsRef, where('content_id', '==', contentId));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({id: doc.id, ...doc.data()}));
    } catch (error) {
      console.error('Error getting reports:', error);
      throw error;
    }
  }

  static async moderateContent(contentId, contentType, action, moderatorId) {
    console.log('Moderating content:', contentId, contentType, action, moderatorId);
    try {
      if (contentType === 'post') {
        const { postDoc, collectionName } = await this.getPost(contentId);
        if (!postDoc) {
          throw new Error('Post not found');
        }
        
        const postRef = doc(db, collectionName, contentId);
        await updateDoc(postRef, {
          deleted: true,
          deleted_at: Timestamp.now(),
          deleted_by: moderatorId,
          moderation_action: action
        });
      } else if (contentType === 'comment') {
        // Handle comment moderation
        // We'll need to traverse the reply path to find the comment
        const commentRef = doc(db, `community_board/${contentId}`);
        await updateDoc(commentRef, {
          deleted: true,
          deleted_at: Timestamp.now(),
          deleted_by: moderatorId,
          moderation_action: action
        });
      }
      return true;
    } catch (error) {
      console.error('Error moderating content:', error);
      throw error;
    }
  }
}